import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Download, Send, Loader2, ExternalLink, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DiligenciaDocument } from '@/features/diligencias/hooks';

const signStatusConfig: Record<string, { label: string; className: string }> = {
  pendente: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-700' },
  enviado: { label: 'Enviado p/ Assinatura', className: 'bg-blue-100 text-blue-700' },
  assinado: { label: 'Assinado', className: 'bg-green-100 text-green-700' },
  recusado: { label: 'Recusado', className: 'bg-red-100 text-red-700' },
};

interface DiligenciaSignatureDocumentsProps {
  documents: DiligenciaDocument[];
  onDownload: (filePath: string) => void;
  /** @deprecated Incorporador não controla envio para assinatura — uso reservado ao admin */
  onSendToClicksign?: (document: DiligenciaDocument) => void;
  isSendingClicksign?: boolean;
  sendingDocumentId?: string | null;
  /** Se true, oculta o botão "Enviar para Assinatura" (visão do incorporador) */
  hideSignAction?: boolean;
}

export const DiligenciaSignatureDocuments: React.FC<DiligenciaSignatureDocumentsProps> = ({
  documents,
  onDownload,
  onSendToClicksign,
  isSendingClicksign = false,
  sendingDocumentId = null,
  hideSignAction = false,
}) => {
  const signatureDocs = documents.filter((d) => d.sign_status !== null);

  if (signatureDocs.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Documentos para Assinatura
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {signatureDocs.map((doc) => {
            const config = signStatusConfig[doc.sign_status!] || signStatusConfig.pendente;
            const isSendingThis = isSendingClicksign && sendingDocumentId === doc.id;

            return (
              <div
                key={doc.id}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-5 w-5 text-red-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{doc.file_name}</p>
                    {doc.clicksign_envelope_id && (
                      <p className="text-xs text-muted-foreground truncate">
                        Envelope: {doc.clicksign_envelope_id}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={cn('text-xs px-2 py-0.5', config.className)}>
                    {config.label}
                  </Badge>

                  {/* Preview do documento - disponível em qualquer status */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDownload(doc.file_path)}
                    title="Visualizar documento"
                    className="h-8 w-8"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>

                  {/* Enviado + tem URL → botao "Assinar" */}
                  {doc.sign_status === 'enviado' && doc.clicksign_request_url && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => window.open(doc.clicksign_request_url!, '_blank')}
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Assinar
                    </Button>
                  )}

                  {/* Pendente → botao "Enviar para Assinatura" (oculto para incorporador) */}
                  {!hideSignAction && doc.sign_status === 'pendente' && onSendToClicksign && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSendToClicksign(doc)}
                      disabled={isSendingClicksign}
                    >
                      {isSendingThis ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="h-3.5 w-3.5 mr-1" />
                          Enviar para Assinatura
                        </>
                      )}
                    </Button>
                  )}

                  {/* Assinado → botao "Baixar" */}
                  {doc.sign_status === 'assinado' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDownload(doc.file_path)}
                      title="Baixar documento assinado"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
